import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ProductAccounts } from 'services/user'
import { useAccountStore, useUserStore } from 'store'

export const useProtectionContext = (enabled = true) => {
	const account = useAccountStore((state) => state.account)
	const userId = useUserStore((state) => state.user?.id)
	const accountId = account?.id
	const queryClient = useQueryClient()
	const [, tick] = useState(0)
	const key = ['protection-context', accountId, userId]
	const projectionKey = `${accountId}:${userId}:${account?.protection?.serverNow}`
	const lastProjection = useRef(projectionKey)
	const query = useQuery(
		key,
		async ({ signal }) => ({
			status: await ProductAccounts.getProtectionStatus(accountId!, signal),
			receivedAt: performance.now()
		}),
		{
			enabled:
				enabled && !!accountId && !!userId && accountId !== 'workspace-demo',
			staleTime: 15000,
			retry: 1,
			refetchOnWindowFocus: 'always',
			refetchInterval: 60000,
			refetchIntervalInBackground: false
		}
	)
	useEffect(() => {
		if (lastProjection.current !== projectionKey && enabled && accountId)
			void queryClient.invalidateQueries(key)
		lastProjection.current = projectionKey
	}, [accountId, userId, account?.protection?.serverNow, enabled, queryClient])
	const liveProtection = query.data?.status.protection
	const protection =
		account?.protection?.serverNow &&
		(!liveProtection?.serverNow ||
			Date.parse(account.protection.serverNow) >
				Date.parse(liveProtection.serverNow))
			? account.protection
			: liveProtection || account?.protection
	const serverNow = query.data?.status.protection?.serverNow
	const now =
		serverNow && query.data
			? Date.parse(serverNow) + performance.now() - query.data.receivedAt
			: Date.now()
	const metered =
		(account?.billing?.subscription?.type !== 'TRIAL' ||
			protection?.boostActive) &&
		account?.billing?.subscription?.plan?.tier !== 'legacy'
	const boundary = metered
		? Math.min(
				protection?.boostActive && protection.boostEndsAt
					? Date.parse(protection.boostEndsAt)
					: Infinity,
				protection?.usagePeriodEnd
					? Date.parse(protection.usagePeriodEnd)
					: Infinity
		  )
		: Infinity
	const stale = Number.isFinite(boundary) && now >= boundary
	useEffect(() => {
		if (!enabled || !Number.isFinite(boundary)) return
		let timer: ReturnType<typeof setTimeout>
		const startedAt = performance.now()
		const check = () => {
			const remaining = boundary - now - (performance.now() - startedAt)
			if (remaining <= 0) {
				tick((value) => value + 1)
				void queryClient.invalidateQueries(key)
			} else timer = setTimeout(check, Math.min(remaining + 1, 86400000))
		}
		check()
		return () => clearTimeout(timer)
	}, [boundary, query.data, accountId, userId, enabled, queryClient])

	return {
		protection,
		stale,
		status:
			stale || protection !== liveProtection ? undefined : query.data?.status,
		loading: query.isLoading
	}
}
